"use client";

import { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ImagePlus, X, ChevronLeft, ChevronRight, ArrowUp, BookOpen } from "lucide-react";
import Link from "next/link";
import { cn } from "@/lib/utils";
import { CASE_STUDIES, type CaseStudy } from "@/utils/caseStudiesCatalog";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";
import { telemetry } from "@/lib/telemetry";

// ── Slide data ─────────────────────────────────────────────────────────────

interface QuestionSlide {
  type: "question";
  question: string;
  options: string[];
}

interface WelcomeSlide {
  type: "welcome";
}

interface PortfolioSlide {
  type: "portfolio";
  question: string;
  subtitle: string;
}

interface VisualSlide {
  type: "visual";
  eyebrow: string;
  title: string;
  body: string;
  imageSrc: string;
  imageAlt: string;
  features: string[];
}

type Slide = WelcomeSlide | QuestionSlide | PortfolioSlide | VisualSlide;

const SLIDES: Slide[] = [
  { type: "welcome" },
  {
    type: "question",
    question: "How long have you been investing or trading?",
    options: [
      "Less than 1 year (Beginner)",
      "1–3 years (Intermediate)",
      "3–7 years (Advanced)",
      "7+ years (Professional)",
    ],
  },
  {
    type: "question",
    question: "What's your main goal right now?",
    options: [
      "Make more money in the markets",
      "Automate my trading strategies",
      "Expand into new strategies",
      "Manage and grow my portfolio",
      "Learn and understand markets better",
    ],
  },
  {
    type: "question",
    question: "How often do you trade?",
    options: [
      "Daily",
      "A few times a week",
      "A few times a month",
      "Rarely — mostly buy and hold",
    ],
  },
  {
    type: "question",
    question: "How large is your portfolio?",
    options: [
      "Under $10k",
      "$10k – $50k",
      "$50k – $250k",
      "$250k+",
    ],
  },
  {
    type: "portfolio",
    question: "Describe your portfolio or trading strategy.",
    subtitle: "Your holdings, your style (scalping, swing trading, earnings plays, etc.), or just drop a screenshot.",
  },
  {
    type: "question",
    question: "Last question, how did you hear about Xynth?",
    options: [
      "Twitter / X",
      "YouTube or TikTok",
      "Reddit or an online community",
      "A friend or colleague",
    ],
  },
];

// ── Typewriter hook (reveal-in-place) ──────────────────────────────────────
// Returns charCount so we can clip with CSS instead of changing text content.
// The full text is always rendered (reserves layout), and a clip mask reveals it.

function useRevealCount(length: number, speed: number = 12, startDelay: number = 100) {
  const [count, setCount] = useState(0);
  const [done, setDone] = useState(false);

  useEffect(() => {
    setCount(0);
    setDone(false);
    let i = 0;
    let cancelled = false;

    const timeout = setTimeout(() => {
      const interval = setInterval(() => {
        if (cancelled) return;
        if (i < length) {
          i++;
          setCount(i);
        } else {
          setDone(true);
          clearInterval(interval);
        }
      }, speed);
      return () => clearInterval(interval);
    }, startDelay);

    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, [length, speed, startDelay]);

  return { count, done };
}

// ── RevealText — renders full text for layout, clips to show only revealed chars

function RevealText({
  text,
  revealCount,
  done,
  className,
  showCursor = true,
}: {
  text: string;
  revealCount: number;
  done: boolean;
  className?: string;
  showCursor?: boolean;
}) {
  return (
    <span className={`relative inline ${className ?? ""}`}>
      {/* Visible portion */}
      <span>{text.slice(0, revealCount)}</span>
      {/* Cursor — zero-width wrapper prevents layout shift; keeps pulsing after done */}
      {showCursor && revealCount > 0 && (
        <span className="inline-block w-0 overflow-visible align-baseline">
          <span className={`text-white/40 ${
            done ? "animate-[pulse_1.4s_ease-in-out_infinite]" : "animate-pulse"
          }`}>|</span>
        </span>
      )}
      {/* Invisible remainder — reserves space so layout doesn't shift */}
      <span className="invisible">{text.slice(revealCount)}</span>
    </span>
  );
}

// ── Welcome slide ─────────────────────────────────────────────────────────

function WelcomeView({ onContinue }: { onContinue: () => void }) {
  const { user } = useAuth();
  const firstName = user?.displayName?.split(" ")[0] || "";

  const line1 = firstName
    ? `Hey ${firstName}, welcome to the future of financial markets.`
    : "Welcome to the future of financial markets.";
  const line2 = "Let's get you set up.";

  const r1 = useRevealCount(line1.length, 24, 300);
  const line2Delay = 300 + line1.length * 24 + 350; // after line1 finishes + short pause
  const r2 = useRevealCount(line2.length, 24, line2Delay);

  useEffect(() => {
    if (!r2.done) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Enter") {
        e.preventDefault();
        e.stopPropagation();
        onContinue();
      }
    };
    window.addEventListener("keydown", handler, true);
    return () => window.removeEventListener("keydown", handler, true);
  }, [r2.done, onContinue]);

  return (
    <div className="flex flex-col px-6 sm:px-8">
      <div className="max-w-[520px]">
        <div className="flex items-start gap-2.5 mb-5">
          {/* Green pulsing dot */}
          <div className="relative flex h-3 w-3 mt-3.5 flex-shrink-0">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" style={{ animationDuration: "1.5s" }} />
            <span className="relative inline-flex rounded-full h-3 w-3 bg-green-400" />
          </div>
          <div className="text-3xl sm:text-4xl font-semibold text-white tracking-tight">
            <h1>
              <RevealText text={line1} revealCount={r1.count} done={r1.done} showCursor={!r1.done || r2.count === 0} />
            </h1>
            <h1 className="mt-2">
              <RevealText text={line2} revealCount={r2.count} done={r2.done} showCursor={r2.count > 0} />
            </h1>
          </div>
        </div>

        {/* Always reserve space for button area so text doesn't shift */}
        <div className="ml-[22px] pt-2">
          <div className={`transition-opacity duration-300 ${r2.done ? "opacity-100" : "opacity-0 pointer-events-none"}`}>
            <button
              onClick={onContinue}
              className="inline-flex items-center gap-2 px-6 py-2 rounded-lg bg-white text-black text-[15px] font-medium hover:opacity-90 transition-opacity"
            >
              Get started
              <span className="text-black translate-y-[1px]">↵</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Question slide ────────────────────────────────────────────────────────

function QuestionView({
  slide,
  index,
  selected,
  onSelect,
}: {
  slide: QuestionSlide;
  index: number;
  selected: string | null;
  onSelect: (option: string) => void;
}) {
  const r = useRevealCount(slide.question.length, 16, 120);
  const [flashIdx, setFlashIdx] = useState<number | null>(null);
  const [showOther, setShowOther] = useState(false);
  const [otherText, setOtherText] = useState("");

  const handleClick = (opt: string, i: number) => {
    setShowOther(false);
    setFlashIdx(i);
    onSelect(opt);
    setTimeout(() => setFlashIdx(null), 260);
  };

  return (
    <div className="flex flex-col px-6 sm:px-8">
      <div className="max-w-[520px]">
        {/* Question */}
        <div className="flex items-start gap-2.5 mb-4">
          {/* Green pulsing dot */}
          <div className="relative flex h-3 w-3 mt-3 flex-shrink-0">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" style={{ animationDuration: "1.5s" }} />
            <span className="relative inline-flex rounded-full h-3 w-3 bg-green-400" />
          </div>
          <h2 className="text-2xl sm:text-[28px] font-medium text-white leading-snug tracking-tight">
            <RevealText text={slide.question} revealCount={r.count} done={r.done} />
          </h2>
        </div>

        {/* Options */}
        <div className="space-y-2.5 ml-[22px]">
          {slide.options.map((opt, i) => {
            const isSelected = selected === opt;
            const isFlashing = flashIdx === i;
            return (
              <motion.button
                key={opt}
                initial={false}
                animate={{
                  opacity: r.done ? 1 : 0,
                  y: r.done ? 0 : 8,
                }}
                transition={{ duration: 0.18, delay: r.done ? i * 0.045 : 0 }}
                onClick={() => r.done && handleClick(opt, i)}
                disabled={!r.done}
                className={`
                  group relative flex items-center w-full text-left px-4 py-3 rounded-md border transition-colors duration-150
                  ${
                    isSelected
                      ? "border-white/50 bg-white/[0.08]"
                      : "border-white/20 hover:border-white/40 hover:bg-white/[0.04]"
                  }
                `}
              >
                {/* Selection glow */}
                {isFlashing && (
                  <motion.div
                    className="absolute inset-0 rounded-md bg-foreground/10"
                    initial={{ opacity: 1 }}
                    animate={{ opacity: 0 }}
                    transition={{ duration: 0.3, ease: "easeOut" }}
                  />
                )}
                <span
                  className={`text-[15px] transition-colors duration-150 ${
                    isSelected ? "text-white font-medium" : "text-white/80 group-hover:text-white"
                  }`}
                >
                  {opt}
                </span>
              </motion.button>
            );
          })}

          {/* Other option */}
          <motion.div
            initial={false}
            animate={{
              opacity: r.done ? 1 : 0,
              y: r.done ? 0 : 8,
            }}
            transition={{ duration: 0.18, delay: r.done ? slide.options.length * 0.045 : 0 }}
          >
            {!showOther ? (
              <button
                onClick={() => r.done && setShowOther(true)}
                disabled={!r.done}
                className="group flex items-center w-full text-left px-4 py-3 rounded-md border border-dashed border-white/15 hover:border-white/30 hover:bg-white/[0.03] transition-colors duration-150"
              >
                <span className="text-[15px] text-white/45 group-hover:text-white/65 transition-colors">
                  Other — type your own
                </span>
              </button>
            ) : (
              <div className="space-y-2">
                <input
                  type="text"
                  value={otherText}
                  onChange={(e) => setOtherText(e.target.value)}
                  placeholder="Type your answer..."
                  autoFocus
                  className="w-full bg-transparent border border-white/30 rounded-md px-4 py-3 text-[15px] text-white placeholder:text-white/25 focus:outline-none focus:border-white/50 transition-colors"
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && otherText.trim()) {
                      onSelect(otherText.trim());
                    }
                  }}
                />
                <button
                  onClick={() => otherText.trim() && onSelect(otherText.trim())}
                  disabled={!otherText.trim()}
                  className="px-5 py-2 rounded-lg bg-white text-black text-[14px] font-medium hover:opacity-90 transition-opacity disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  Continue
                </button>
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
}

function VisualView({
  slide,
}: {
  slide: VisualSlide;
}) {
  const r = useRevealCount(slide.title.length, 13, 120);

  return (
    <div className="flex flex-col px-6 sm:px-8">
      <div className="max-w-[640px] w-full">
        <div className="mb-4">
          <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-3 py-1">
            <span className="relative flex h-1.5 w-1.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-sky-300 opacity-75" style={{ animationDuration: "1.5s" }} />
              <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-sky-300" />
            </span>
            <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-sky-100/85">
              {slide.eyebrow}
            </span>
          </div>
          <h2 className="text-2xl sm:text-[28px] font-medium text-white leading-snug tracking-tight">
            <RevealText text={slide.title} revealCount={r.count} done={r.done} />
          </h2>
          <p className={`mt-3 text-[14px] leading-relaxed text-white/70 transition-opacity duration-300 ${r.done ? "opacity-100" : "opacity-0"}`}>
            {slide.body}
          </p>
        </div>

        <div className={`transition-opacity duration-300 ${r.done ? "opacity-100" : "opacity-0 pointer-events-none"}`}>
          <div className="relative overflow-hidden rounded-xl border border-white/10 bg-zinc-950">
            <img
              src={slide.imageSrc}
              alt={slide.imageAlt}
              className="block w-full max-h-[260px] object-cover object-top"
            />
            <div className="pointer-events-none absolute inset-0 shadow-[inset_0_-60px_80px_-60px_rgba(0,0,0,0.8)]" />
          </div>

          <div className="mt-3 grid gap-2 sm:grid-cols-3">
            {slide.features.map((feature) => (
              <div key={feature} className="rounded-lg border border-white/10 bg-white/[0.04] px-3 py-2.5">
                <p className="text-[12.5px] leading-snug text-white/80">{feature}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Portfolio slide ───────────────────────────────────────────────────────

function PortfolioView({
  slide,
  portfolioText,
  portfolioImage,
  onTextChange,
  onImageChange,
  onContinue,
}: {
  slide: PortfolioSlide;
  portfolioText: string;
  portfolioImage: File | null;
  onTextChange: (text: string) => void;
  onImageChange: (file: File | null) => void;
  onContinue: () => void;
}) {
  const r = useRevealCount(slide.question.length, 16, 120);
  const rSub = useRevealCount(slide.subtitle.length, 7, r.done ? 0 : 99999);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);

  const handleFile = (file: File) => {
    if (!file.type.startsWith("image/")) return;
    onImageChange(file);
    const reader = new FileReader();
    reader.onload = (e) => setImagePreview(e.target?.result as string);
    reader.readAsDataURL(file);
  };

  const removeImage = () => {
    onImageChange(null);
    setImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  };

  const canContinue = portfolioText.trim().length > 0 || portfolioImage !== null;

  // Enter to continue
  useEffect(() => {
    if (!canContinue || !rSub.done) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Enter" && !e.shiftKey) {
        // Don't capture if user is typing in the textarea
        if (document.activeElement?.tagName === "TEXTAREA") return;
        e.preventDefault();
        e.stopPropagation();
        onContinue();
      }
    };
    window.addEventListener("keydown", handler, true);
    return () => window.removeEventListener("keydown", handler, true);
  }, [canContinue, rSub.done, onContinue]);

  return (
    <div className="flex flex-col px-6 sm:px-8">
      <div className="max-w-[520px] w-full">
        {/* Question */}
        <div className="flex items-start gap-2.5 mb-2">
          {/* Green pulsing dot */}
          <div className="relative flex h-3 w-3 mt-3 flex-shrink-0">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" style={{ animationDuration: "1.5s" }} />
            <span className="relative inline-flex rounded-full h-3 w-3 bg-green-400" />
          </div>
          <h2 className="text-2xl sm:text-[28px] font-medium text-white leading-snug tracking-tight">
            <RevealText text={slide.question} revealCount={r.count} done={r.done} />
          </h2>
        </div>

        {/* Subtitle */}
        <p className="text-[14px] text-white/70 ml-[22px] mb-4">
          <RevealText text={slide.subtitle} revealCount={rSub.count} done={rSub.done} showCursor={false} />
        </p>

        {/* Input area — always rendered, fade in after subtitle done */}
        <div className={`ml-[22px] space-y-3 transition-opacity duration-300 ${rSub.done ? "opacity-100" : "opacity-0 pointer-events-none"}`}>
          {/* Text area */}
          <textarea
            value={portfolioText}
            onChange={(e) => onTextChange(e.target.value)}
            placeholder="e.g. Mostly big pharma and Tesla, some SPY calls, a bit of ETH..."
            rows={3}
            className="w-full bg-transparent border border-white/25 rounded-md px-4 py-3 text-[15px] text-white placeholder:text-white/50 focus:outline-none focus:border-white/45 resize-none transition-colors"
          />

          {/* Image upload zone */}
          {!imagePreview ? (
            <div
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              className={`
                flex items-center gap-3 px-3.5 py-3 rounded-md border border-dashed cursor-pointer transition-colors
                ${dragOver ? "border-white/40 bg-white/[0.04]" : "border-white/20 hover:border-white/30"}
              `}
            >
              <ImagePlus className="w-4 h-4 text-white/60 flex-shrink-0" />
              <span className="text-[14px] text-white/65">
                Drop a screenshot of your portfolio, or click to upload
              </span>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleFile(file);
                }}
              />
            </div>
          ) : (
            <div className="relative inline-block">
              <img
                src={imagePreview}
                alt="Portfolio screenshot"
                className="max-h-[120px] rounded-md border border-white/20 object-contain"
              />
              <button
                onClick={removeImage}
                className="absolute -top-2 -right-2 p-1 rounded-full bg-black border border-white/30 text-white/50 hover:text-white transition-colors"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          )}

          {/* Continue button */}
          <div className="pt-2">
            <button
              onClick={onContinue}
              disabled={!canContinue}
              className="px-6 py-2.5 rounded-lg bg-white text-black text-[15px] font-medium hover:opacity-90 transition-opacity disabled:opacity-30 disabled:cursor-not-allowed"
            >
              Continue
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Free Trial slide (final) ──────────────────────────────────────────────

const FALLBACK_PILLS = [
  "Why is NVDA moving today?",
  "What changed in markets this week?",
  "Build me a daily market brief",
  "Build an alert for NVDA options flow",
  "Screen for insider buying with strong momentum",
  "Find sectors getting fresh smart-money flow",
  "Backtest buying SPY dips after red weeks",
  "Would this earnings strategy have worked?",
  "Am I overexposed to tech right now?",
  "What should I watch in my portfolio?",
];

function AutoScrollPills({ prompts, onPillClick }: { prompts: string[]; onPillClick: () => void }) {
  const half = Math.ceil(prompts.length / 2);
  const row1 = prompts.slice(0, half);
  const row2 = prompts.slice(half);

  const truncate = (text: string) => {
    const words = text.split(" ");
    return words.length > 10 ? words.slice(0, 10).join(" ") + "…" : text;
  };

  return (
    <div className="relative overflow-hidden group/carousel">
      {/* Left/right fade masks */}
      <div className="absolute left-0 top-0 bottom-0 w-12 bg-gradient-to-r from-black to-transparent z-10 pointer-events-none" />
      <div className="absolute right-0 top-0 bottom-0 w-12 bg-gradient-to-l from-black to-transparent z-10 pointer-events-none" />

      {/* Row 1 — scrolls left */}
      <div className="flex gap-2 mb-2 overflow-hidden">
        <div
          className="flex gap-2 group-hover/carousel:[animation-play-state:paused]"
          style={{ animation: "scrollLeft 55s linear infinite" }}
        >
          {[...row1, ...row1].map((pill, i) => (
            <button
              key={`r1-${i}`}
              onClick={onPillClick}
              className="group/pill flex-shrink-0 inline-flex items-center gap-2 pl-3.5 pr-2 py-1.5 rounded-full border border-white/10 text-[13px] text-white/70 bg-white/[0.04] hover:bg-white/[0.08] hover:border-white/20 hover:text-white transition-all cursor-pointer whitespace-nowrap"
            >
              {truncate(pill)}
              <span className="flex items-center justify-center w-6 h-6 rounded-full bg-white text-black flex-shrink-0 group-hover/pill:bg-zinc-200 transition-colors">
                <ArrowUp className="w-3 h-3" />
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Row 2 — scrolls right (opposite direction) */}
      <div className="flex gap-2 overflow-hidden">
        <div
          className="flex gap-2 group-hover/carousel:[animation-play-state:paused]"
          style={{ animation: "scrollRight 60s linear infinite" }}
        >
          {[...row2, ...row2].map((pill, i) => (
            <button
              key={`r2-${i}`}
              onClick={onPillClick}
              className="group/pill flex-shrink-0 inline-flex items-center gap-2 pl-3.5 pr-2 py-1.5 rounded-full border border-white/10 text-[13px] text-white/70 bg-white/[0.04] hover:bg-white/[0.08] hover:border-white/20 hover:text-white transition-all cursor-pointer whitespace-nowrap"
            >
              {truncate(pill)}
              <span className="flex items-center justify-center w-6 h-6 rounded-full bg-white text-black flex-shrink-0 group-hover/pill:bg-zinc-200 transition-colors">
                <ArrowUp className="w-3 h-3" />
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function OnboardingBlogCard({ study }: { study: CaseStudy }) {
  return (
    <Link
      href={`/blog/${study.slug}`}
      className="group relative block flex-shrink-0 w-[220px] cursor-pointer"
    >
      <div className={cn(
        "relative rounded-xl border overflow-hidden bg-[#0a0a0b]",
        "transition-all duration-300",
        "border-white/[0.06]",
        "hover:border-white/[0.12] hover:-translate-y-0.5"
      )}>
        <div className="relative w-full aspect-[16/10] overflow-hidden">
          {study.thumbnailUrl ? (
            <img
              src={study.thumbnailUrl}
              alt={study.headline}
              className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.03]"
              loading="lazy"
            />
          ) : (
            <div className="absolute inset-0 bg-zinc-900/50" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0b] via-[#0a0a0b]/40 to-transparent" />

          {/* Return badge */}
          {study.returnPct !== "Guide" && (
            <div className="absolute top-2 right-2 z-20 px-2 py-0.5 rounded-md bg-emerald-500/20 backdrop-blur-sm border border-emerald-500/30">
              <span className="text-[11px] font-semibold text-emerald-400">{study.returnPct}</span>
            </div>
          )}

          {/* Read icon on hover */}
          <div className={cn(
            "absolute inset-0 z-20 flex items-center justify-center pointer-events-none transition-opacity duration-300",
            "opacity-0 group-hover:opacity-100"
          )}>
            <div className="w-9 h-9 rounded-full flex items-center justify-center bg-white/[0.1] backdrop-blur-sm border border-white/[0.15]">
              <BookOpen className="w-3.5 h-3.5 text-white/90" />
            </div>
          </div>
        </div>

        <div className="px-3 pt-2 pb-2.5">
          <h3 className="text-white/90 font-medium text-[13px] leading-tight line-clamp-2">
            {study.headline}
          </h3>
          <p className="text-white/40 text-[11px] mt-1">{study.readTime} read · {study.strategy}</p>
        </div>
      </div>
    </Link>
  );
}

function AnimatedDots() {
  return <span className="dots-animation" />;
}

function AnalyzingView() {
  const [stepIdx, setStepIdx] = useState(0);
  const steps = [
    "Analyzing your profile",
    "Crafting your experience",
    "Personalizing recommendations",
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setStepIdx((i) => (i < steps.length - 1 ? i + 1 : i));
    }, 1800);
    return () => clearInterval(interval);
  }, [steps.length]);

  return (
    <div className="flex flex-col px-6 sm:px-8">
      <div className="max-w-[520px]">
        <div className="flex items-start gap-2.5">
          {/* Green pulsing dot — same as every other view */}
          <div className="relative flex h-3 w-3 mt-3 flex-shrink-0">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" style={{ animationDuration: "1.5s" }} />
            <span className="relative inline-flex rounded-full h-3 w-3 bg-green-400" />
          </div>
          <AnimatePresence mode="wait">
            <motion.h2
              key={stepIdx}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.3 }}
              className="text-2xl sm:text-[28px] font-medium text-white tracking-tight"
            >
              {steps[stepIdx]}<AnimatedDots />
            </motion.h2>
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}


// ── Main component ────────────────────────────────────────────────────────

interface SavedProgress {
  answers: (string | null)[];
  portfolio_text: string;
  last_completed_stage: number;
}

interface OnboardingTypeformProps {
  open: boolean;
  onComplete: (data: { answers: string[]; portfolioText: string; portfolioImage: File | null; selectedPrompt?: string }) => void;
  onDismiss?: (data: { answers: string[]; portfolioText: string; portfolioImage: File | null; selectedPrompt?: string }) => void;
  onOpenPlans: (data?: { answers: string[]; portfolioText: string; portfolioImage: File | null; selectedPrompt?: string }) => void;
  onProgress?: (stage: number, answers: (string | null)[], portfolioText: string) => void;
  savedProgress?: SavedProgress | null;
}

export function OnboardingTypeform({ open, onComplete, onDismiss, onOpenPlans, onProgress, savedProgress }: OnboardingTypeformProps) {
  const numQuestions = SLIDES.filter((s) => s.type === "question").length;

  // Resume from saved progress: start at the slide AFTER the last completed one
  const initialSlide = savedProgress ? Math.min(savedProgress.last_completed_stage + 1, SLIDES.length - 1) : 0;
  const initialAnswers = savedProgress?.answers?.length === numQuestions
    ? savedProgress.answers
    : SLIDES.filter((s) => s.type === "question").map(() => null);

  const [currentSlide, setCurrentSlide] = useState(initialSlide);
  const [answers, setAnswers] = useState<(string | null)[]>(initialAnswers);
  const [portfolioText, setPortfolioText] = useState(savedProgress?.portfolio_text || "");
  const [portfolioImage, setPortfolioImage] = useState<File | null>(null);
  const autoAdvanceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [showDismissGuard, setShowDismissGuard] = useState(false);

  const totalSlides = SLIDES.length;
  const slide = SLIDES[currentSlide];
  const questionIndex = SLIDES.slice(0, currentSlide + 1).filter((s) => s.type === "question").length;

  const completionData = useCallback(() => ({
    answers: answers.filter((a): a is string => a !== null),
    portfolioText,
    portfolioImage,
  }), [answers, portfolioText, portfolioImage]);

  // ── Navigation ──────────────────────────────────────────────────────
  const goNext = useCallback(() => {
    // Save progress for non-question slides (questions are saved in handleSelect)
    if (slide.type !== "question") {
      onProgress?.(currentSlide, answers, portfolioText);
    }

    // Emit one stage_completed event per slide leave so we can build
    // the per-stage drop-off funnel. Note: question slides also auto-
    // advance via handleSelect's setTimeout → goNext, so this catches
    // both manual Next-button and auto-advance paths through a single
    // emit site.
    telemetry.emit("onboarding.questionnaire_stage_completed", {
      slide_index: currentSlide,
      slide_type: slide.type,
      total_slides: totalSlides,
      answer_at_slide:
        slide.type === "question"
          ? answers[
              SLIDES.slice(0, currentSlide + 1).filter((s) => s.type === "question").length - 1
            ] ?? null
          : null,
      is_last_slide: currentSlide >= totalSlides - 1,
    });

    if (currentSlide < totalSlides - 1) {
      setCurrentSlide((c) => c + 1);
    } else {
      // Questionnaire complete - call onComplete instead of showing trial phase
      onComplete(completionData());
    }
  }, [currentSlide, totalSlides, slide, answers, portfolioText, onProgress, onComplete, completionData]);

  const goBack = useCallback(() => {
    if (currentSlide > 0) setCurrentSlide((c) => c - 1);
  }, [currentSlide]);

  const handleSelect = useCallback(
    (option: string) => {
      let qIdx = 0;
      for (let i = 0; i <= currentSlide; i++) {
        if (SLIDES[i].type === "question") {
          if (i === currentSlide) break;
          qIdx++;
        }
      }

      const updatedAnswers = [...answers];
      updatedAnswers[qIdx] = option;
      setAnswers(updatedAnswers);

      // Save progress for this question slide
      onProgress?.(currentSlide, updatedAnswers, portfolioText);

      if (autoAdvanceTimer.current) clearTimeout(autoAdvanceTimer.current);
      autoAdvanceTimer.current = setTimeout(() => {
        goNext();
      }, 320);
    },
    [currentSlide, goNext, answers, portfolioText, onProgress]
  );

  // ── Completion handler (dismiss / close) ───────────────────────────
  const handleDismiss = useCallback(() => {
    onProgress?.(currentSlide, answers, portfolioText);
    onDismiss?.(completionData());
  }, [answers, portfolioText, onDismiss, onProgress, currentSlide, completionData]);

  const requestDismissGuard = useCallback(() => {
    setShowDismissGuard(true);
  }, []);

  const handleStayInOnboarding = useCallback(() => {
    setShowDismissGuard(false);
  }, []);

  const handleConfirmDismiss = useCallback(() => {
    setShowDismissGuard(false);
    telemetry.emit("onboarding.questionnaire_dismissed", {
      slide_index_at_dismiss: currentSlide,
      answered_count: answers.filter((a) => a !== null).length,
      total_questions: numQuestions,
    });
    handleDismiss();
  }, [handleDismiss, currentSlide, answers, numQuestions]);

  useEffect(() => {
    return () => {
      if (autoAdvanceTimer.current) clearTimeout(autoAdvanceTimer.current);
    };
  }, []);

  // Emit a one-shot `questionnaire_open` event the first time the
  // typeform actually becomes visible for a session. We deliberately
  // gate on `open` going from false→true (not just mount) because the
  // parent <OnboardingManager> renders the typeform with `open=false`
  // while it's still deciding whether to show it.
  const openedRef = useRef(false);
  useEffect(() => {
    if (!open || openedRef.current) return;
    openedRef.current = true;
    telemetry.emit("onboarding.questionnaire_open", {
      total_slides: totalSlides,
      resumed_from_stage: savedProgress?.last_completed_stage ?? null,
      initial_slide: initialSlide,
    });
  }, [open, totalSlides, savedProgress, initialSlide]);

  if (!open) return null;

  // Content key for AnimatePresence
  const contentKey = `slide-${currentSlide}`;

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent
        className="sm:max-w-[720px] w-[95vw] p-0 gap-0 flex flex-col overflow-visible border-none bg-transparent shadow-none [&>button:last-of-type]:hidden"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogTitle className="sr-only">Welcome to Xynth</DialogTitle>

        {/* Fading-edge background container */}
        <motion.div
          layout
          transition={{ layout: { duration: 0.28, ease: [0.25, 0.1, 0.25, 1] } }}
          className="relative overflow-hidden rounded-2xl max-h-[85vh] bg-black border border-white/25"
        >
          {/* X button — always closes/dismisses onboarding (visible after welcome slide) */}
          {currentSlide > 0 ? (
            <button
              onClick={requestDismissGuard}
              className="absolute top-4 right-4 z-20 p-1.5 rounded-full text-white/30 hover:text-white/70 hover:bg-white/10 transition-all"
            >
              <X className="w-4 h-4" />
            </button>
          ) : null}
          <AnimatePresence mode="wait">
            <motion.div
              key={contentKey}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -30 }}
              transition={{ duration: 0.2, ease: "easeInOut" }}
              className="py-12 sm:py-14"
            >
              {slide.type === "welcome" ? (
                <WelcomeView onContinue={goNext} />
              ) : slide.type === "visual" ? (
                <VisualView slide={slide} />
              ) : slide.type === "portfolio" ? (
                <PortfolioView
                  slide={slide}
                  portfolioText={portfolioText}
                  portfolioImage={portfolioImage}
                  onTextChange={setPortfolioText}
                  onImageChange={setPortfolioImage}
                  onContinue={goNext}
                />
              ) : (
                <QuestionView
                  slide={slide as QuestionSlide}
                  index={questionIndex}
                  selected={answers[questionIndex - 1]}
                  onSelect={handleSelect}
                />
              )}

            </motion.div>
          </AnimatePresence>

          {/* Navigation — icon-only back / next */}
          {currentSlide > 0 && (
            <div className="flex items-center justify-between px-6 sm:px-8 pb-4 pt-1">
              <button
                onClick={goBack}
                className="flex items-center justify-center w-8 h-8 rounded-lg border border-white/15 text-white/40 hover:text-white/80 hover:border-white/30 transition-all"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                onClick={goNext}
                className="flex items-center justify-center w-8 h-8 rounded-lg border border-white/15 text-white/40 hover:text-white/80 hover:border-white/30 transition-all"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
          {showDismissGuard ? (
            <div
              className="absolute inset-0 z-30 flex items-center justify-center bg-black/75 px-4"
              role="dialog"
              aria-modal="true"
              aria-labelledby="onboarding-dismiss-guard-title"
            >
              <div className="w-full max-w-md rounded-2xl border border-white/15 bg-zinc-950 px-6 py-5 text-white shadow-2xl">
                <div
                  id="onboarding-dismiss-guard-title"
                  className="text-lg font-semibold tracking-tight"
                >
                  Leave onboarding?
                </div>
                <div className="mt-4 space-y-3 text-sm leading-6 text-zinc-300">
                  <p>
                    You are setting up{" "}
                    <strong className="font-semibold text-white">your Xynth experience</strong>.
                  </p>
                  <p>
                    These answers help Xynth show you{" "}
                    <strong className="font-semibold text-white">better signals</strong>,{" "}
                    <strong className="font-semibold text-white">better workflows</strong>,
                    and{" "}
                    <strong className="font-semibold text-white">more relevant examples</strong>.
                  </p>
                </div>
                <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                  <button
                    type="button"
                    onClick={handleConfirmDismiss}
                    className="rounded-full px-4 py-2 text-sm font-medium text-zinc-400 transition hover:text-red-300"
                  >
                    Leave anyway
                  </button>
                  <button
                    type="button"
                    onClick={handleStayInOnboarding}
                    className="rounded-full bg-white px-4 py-2 text-sm font-semibold text-black transition hover:bg-white/90"
                    autoFocus
                  >
                    Keep going
                  </button>
                </div>
              </div>
            </div>
          ) : null}
        </motion.div>
      </DialogContent>
    </Dialog>
  );
}
